import { createContext, useCallback, useEffect, useState } from "react";
import React from "react";
import { ExtendSearchSite } from "../dto/ExtendSearchSite";
import { useEvent } from "../hooks/useEvents";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { ResourceView } from "../dto/ResourceView";
import { AvailabilitySource } from "../contexts/EResourceAvailabilityContext";
import { ResourceDWR } from "../dto/ResourceDWR";
import { ListType } from "../components/pages/account/ListService";
import { useAccount } from "../components/Account";

/*
https://support.google.com/analytics/answer/9234069?hl=en
The value assigned to event parameters must be 100 characters or fewer. The page_title parameter must be 300 characters or fewer. The page_referrer parameter must be 420 characters or fewer. The page_location parameter must be 1,000 characters or fewer.
If you override the page_location parameter, make sure that the URL path is valid. If you assign an invalid URL path, the Page location dimension will be empty. You can use the Campaign URL Builder to check whether a URL path is valid.
*/

// URL paths seem to be fine as relative, but we may want to use the full URL for customer and something like https://ls2pac.com/ for community. This would impact length limits though.

let isInitialized = false;

declare global {
  interface Window {
    dataLayer: unknown[]; // TODO narrow typing to Commands
  }
}

// Narrowed Gtag.CustomParams list
interface TLCAnalyticsEventParams extends Gtag.EventParams {
  provider_name?: string;
  provider_domain?: string;
  provider_link?: string;
  search_term?: string;
  search_type?: "keyword" | "advanced" | "reading_level";
  ls_result_count?: number;
  ls_result_bucket?: string;
  change_type?: "add" | "remove" | "clear";
  view_mode?: "list" | "grid";
  filter_name?: string;
  filter_value?: string;
  extend_site_name?: string;
  extend_site_url_base?: string;
  list_type?: ListType;
  position?: number;
  direction?: "next" | "prev";
  list_name?: string;
  prev_list_name?: string;
  title?: string;
  item_count?: number;
  default_list?: boolean;
  hold_type?: "title" | "item";
  batch?: boolean;
  user_entered?: boolean;
  tag_name?: string;
  timed_out?: boolean;
}

type TLCAnalyticsEventNames =
  | Gtag.EventNames
  | "extend_search_site_click"
  | "downloadable_availability_click"
  | "downloadable_checkout"
  | "downloadable_external_link_click"
  | "downloadable_place_hold"
  | "forgot_password"
  | "hold_suspend"
  | "hold_place"
  | "hold_reactivate"
  | "hold_cancel"
  | "landing_page_navigate"
  | "landing_page_list_change"
  | "landing_page_select_item"
  | "list_create"
  | "list_delete"
  | "list_rename"
  | "list_remove_item"
  | "list_add_item"
  | "list_share"
  | "logout"
  | "review_add"
  | "review_remove"
  | "save_search"
  | "search_filter_change"
  | "tag_add"
  | "tag_blocked"
  | "virtual_shelf_close"
  | "virtual_shelf_open"
  | "virtual_shelf_navigate"
  | "virtual_shelf_select_item";

// https://developers.google.com/analytics/devguides/collection/ga4/single-page-applications?implementation=event
type SPAConfigParams = Gtag.ConfigParams & {
  page_referrer?: string;
  update?: boolean;
};

async function initGoogleAnalytics() {
  if (isInitialized) return;

  const {
    communityTrackingEnabled,
    communityTrackingCode,
    customerTrackingEnabled,
    customerTrackingCode,
    config,
    contentServerCustomerId,
    libraryType,
    svnRevision,
  } = window;

  // TODO initialize with AdminOrUser dimension set to avoid missing data on first page view or already logged in user
  // TODO custom_map is a UA thing, remove?
  if (communityTrackingEnabled) {
    try {
      window.gtag("config", communityTrackingCode, {
        send_page_view: false,
        // debug_mode: true,
        CustomerId: contentServerCustomerId,
        LibraryType: libraryType,
        Revision: svnRevision,
      });
      console.log(
        "Initialized community tracking with code: ",
        communityTrackingCode,
      );
    } catch (e: unknown) {
      console.error("Error initializing community tracking: ", e);
    }
  }
  if (customerTrackingEnabled) {
    try {
      window.gtag("config", customerTrackingCode, {
        send_page_view: false,
        // debug_mode: true,
        PACConfig: config,
      });
      console.log(
        "Initialized customer tracking with code: ",
        customerTrackingCode,
      );
    } catch (e: unknown) {
      console.error("Error initializing customer tracking: ", e);
    }
  }
  isInitialized = true;
}

interface AnalyticsContextValue {
  trackEvent: (
    name: TLCAnalyticsEventNames,
    eventParams?: TLCAnalyticsEventParams,
    sendTo?: "community" | "customer" | "all",
  ) => void;
  trackPageView: (title: string, location: string, referrer: string) => void;
  trackSearch: (
    term: string,
    resultsCount: number,
    viewMode: "list" | "grid",
  ) => void;
  trackExtendSearchSiteClick: (
    ess: ExtendSearchSite,
    term: string,
    lsResultsCount: number,
  ) => void;
  trackAddFacetFilter: (name: string, value: string) => void;
  trackRemoveFacetFilter: (name: string, value: string) => void;
  trackClearFacetFilters: () => void;
  trackDownloadableClick: (
    name:
      | "downloadable_checkout"
      | "downloadable_availability_click"
      | "downloadable_place_hold"
      | "downloadable_external_link_click",
    resource: ResourceView | ResourceDWR,
    source: AvailabilitySource,
  ) => void;
  trackLogin: () => void;
  trackLogout: (timedOut?: boolean) => void;
  trackPlaceHold: (hold_type: "title" | "item", item_count: number) => void;
  trackSuspendHold: (item_count: number) => void;
  trackReactivateHold: (item_count: number) => void;
  trackCancelHold: (item_count: number) => void;
  trackLandingPageNavigate: (direction: "next" | "prev") => void;
  trackLandingPageListChange: (
    list_name: string,
    prev_list_name: string,
  ) => void;
  trackLandingPageJacketClick: () => void;
}

export const AnalyticsContext = createContext<
  AnalyticsContextValue | undefined
>(undefined);

export const AnalyticsProvider = ({ children }: React.PropsWithChildren) => {
  const {
    communityTrackingEnabled,
    communityTrackingCode,
    customerTrackingEnabled,
    customerTrackingCode,
    contentServerCustomerId,
    libraryType,
    config,
    svnRevision,
  } = window;

  const {
    isAuthenticated,
    isAdmin,
    isConfigAdmin,
    hasChildrenAndCanViewDependentLoansActivity,
    hasChildrenAndCanViewDependentHoldsActivity,
    hasChildrenAndCanViewDependentFinesActivity,
    hasChildrenAndProhibitedListFunctionality,
  } = useAccount();

  const analyticsDisabled =
    !communityTrackingEnabled && !customerTrackingEnabled;

  useEffect(() => {
    if (analyticsDisabled) {
      return;
    }
    if (!isAuthenticated) {
      window.gtag("set", "user_properties", {
        is_authenticated: false,
        is_guardian: false,
        is_admin: false,
        is_config_admin: false,
      });
    } else {
      const isGuardian =
        hasChildrenAndCanViewDependentLoansActivity ||
        hasChildrenAndCanViewDependentHoldsActivity ||
        hasChildrenAndCanViewDependentFinesActivity ||
        hasChildrenAndProhibitedListFunctionality;
      window.gtag("set", "user_properties", {
        is_authenticated: true,
        is_guardian: isGuardian,
        is_admin: isAdmin,
        is_config_admin: isConfigAdmin,
      });
    }
  }, [
    isAuthenticated,
    isAdmin,
    isConfigAdmin,
    hasChildrenAndCanViewDependentLoansActivity,
    hasChildrenAndCanViewDependentHoldsActivity,
    hasChildrenAndCanViewDependentFinesActivity,
    hasChildrenAndProhibitedListFunctionality,
  ]);

  useEffect(() => {
    console.log("communityTrackingEnabled: ", communityTrackingEnabled);
    console.log("customerTrackingEnabled: ", customerTrackingEnabled);
    if (analyticsDisabled) {
      return;
    }
    initGoogleAnalytics();
  }, []);

  const trackEvent = useCallback(
    async (
      name: TLCAnalyticsEventNames,
      eventParams: TLCAnalyticsEventParams = {},
      sendTo: "community" | "customer" | "all" = "all",
    ) => {
      if (analyticsDisabled) return;
      await initGoogleAnalytics();
      const params: Gtag.EventParams & Gtag.ControlParams = {
        ...eventParams,
      };
      if (sendTo === "community") {
        if (!communityTrackingEnabled) {
          return;
        }
        params.send_to = communityTrackingCode;
      }
      if (sendTo === "customer") {
        if (!customerTrackingEnabled) {
          return;
        }
        params.send_to = customerTrackingCode;
      }
      // "all" omits send_to to send to all configured trackers
      window.gtag("event", name, params);
    },
    [],
  );

  const trackPageView = useCallback(
    async (title: string, location: string, referrer: string) => {
      // when and if we migrate to BrowserRouter from HashRouter, we should be able to rely on the History API integration of google analytics and drop this
      // https://developers.google.com/analytics/devguides/collection/ga4/single-page-applications?implementation=event
      // page views go to all enabled trackers, send_to is not needed
      if (analyticsDisabled) return;
      await initGoogleAnalytics();
      const configParams: SPAConfigParams = {
        page_referrer: referrer,
        page_title: title,
        page_location: location,
        send_page_view: false, // page view gets triggered by event
        update: true,
      };

      if (communityTrackingEnabled) {
        const communityCustomDimensions: Gtag.CustomParams = {
          CustomerId: contentServerCustomerId,
          LibraryType: libraryType,
          Revision: svnRevision,
        };
        window.gtag("config", communityTrackingCode, {
          ...configParams,
          ...communityCustomDimensions,
        });
      }
      if (customerTrackingEnabled) {
        const customerCustomDimensions: Gtag.CustomParams = {
          PACConfig: config,
        };
        window.gtag("config", customerTrackingCode, {
          ...configParams,
          ...customerCustomDimensions,
        });
      }
      trackEvent("page_view");
    },
    [],
  );

  function getResultsBucket(resultCount: number) {
    if (resultCount === 0) {
      return "0";
    } else if (resultCount <= 10) {
      return "1-10";
    } else if (resultCount <= 50) {
      return "11-50";
    } else if (resultCount <= 100) {
      return "51-100";
    } else {
      return "100+";
    }
  }

  // TODO paging, sorting, filters...
  // does this inflate searches relative to 5.7.6 analytics?
  // TODO better advanced search, reading level search tracking
  const trackKeywordSearch = useCallback(
    (term: string, resultsCount: number, viewMode: "list" | "grid") => {
      trackEvent("search", {
        search_term: term, // potentially high cardinality, consider omitting
        view_mode: viewMode,
        ls_result_count: resultsCount, // potentially high cardinality, consider omitting
        ls_result_bucket: getResultsBucket(resultsCount), // normalize result count into buckets
        search_type: "keyword",
      });
    },
    [],
  );

  const trackExtendSearchSiteClick = useCallback(
    (ess: ExtendSearchSite, term: string, lsResultsCount: number) => {
      trackEvent("extend_search_site_click", {
        search_type: "keyword",
        search_term: term,
        ls_result_count: lsResultsCount, // potentially high cardinality, consider omitting
        ls_result_bucket: getResultsBucket(lsResultsCount),
        extend_site_name: ess.siteName,
        extend_site_url_base: ess.queryString,
        position: ess.orderIndex,
      });
    },
    [],
  );

  const trackPlaceHold = useCallback(
    (hold_type: "title" | "item", item_count: number) => {
      if (hold_type === "item") {
        trackEvent("hold_place", {
          hold_type,
          batch: false,
          item_count, // potentially high cardinality, consider bucketing
        });
      } else {
        trackEvent("hold_place", {
          hold_type,
          batch: item_count > 1,
          item_count: item_count, // potentially high cardinality, consider bucketing
        });
      }
    },
    [],
  );

  // batch place hold from Saved Lists (can still be single selection)
  useEvent(
    LS2PAC_CONSTANTS.Events.onSubmitRequest,
    (event: {
      data: unknown[];
      pickupBranchId: string | number;
      isTitleLevel: boolean;
    }) => {
      trackPlaceHold(event.isTitleLevel ? "title" : "item", event.data?.length);
    },
  );

  const trackSuspendHold = useCallback((item_count: number) => {
    // Add number of days?
    trackEvent("hold_suspend", {
      batch: item_count > 1,
      item_count: item_count, // potentially high cardinality, consider bucketing
    });
  }, []);

  const trackReactivateHold = useCallback((item_count: number) => {
    trackEvent("hold_reactivate", {
      batch: item_count > 1,
      item_count: item_count, // potentially high cardinality, consider bucketing
    });
  }, []);

  const trackCancelHold = useCallback((item_count: number) => {
    trackEvent("hold_cancel", {
      batch: item_count > 1,
      item_count: item_count, // potentially high cardinality, consider bucketing
    });
  }, []);

  // TODO add error tracking for common but intermittent errors
  // Network errors
  // Virtual shelf errors
  // Syndetics enrichment failures
  // EResource integration failures (availability etc.)

  // !!! COMMUNITY ONLY !!!
  const trackAddFacetFilter = useCallback((name: string, value: string) => {
    trackEvent(
      "search_filter_change",
      {
        change_type: "add",
        filter_name: name,
        filter_value: value,
      },
      "community",
    );
  }, []);
  const trackRemoveFacetFilter = useCallback((name: string, value: string) => {
    trackEvent(
      "search_filter_change",
      {
        change_type: "remove",
        filter_name: name,
        filter_value: value,
      },
      "community",
    );
  }, []);
  const trackClearFacetFilters = useCallback(() => {
    trackEvent(
      "search_filter_change",
      {
        change_type: "clear",
      },
      "community",
    );
  }, []);

  // DEPRECATED! remove during dashboard development
  const trackLandingPageNavigate = useCallback((direction: "next" | "prev") => {
    trackEvent(
      "landing_page_navigate",
      {
        direction,
      },
      "community",
    );
  }, []);

  const trackLandingPageListChange = useCallback(
    (list_name: string, prev_list_name: string) => {
      trackEvent(
        "landing_page_list_change",
        {
          list_name,
          prev_list_name,
        },
        "community",
      );
    },
    [],
  );

  const trackLandingPageJacketClick = useCallback(() => {
    // TODO grab more info about the click if possible (resource title, resource link, standard number, page index, list index, etc.)
    trackEvent("landing_page_select_item", {}, "community");
  }, []);

  useEvent(
    LS2PAC_CONSTANTS.Events.onGetVirtualShelfResources,
    (event: { type: "open" | "previous" | "next"; resource: ResourceDWR }) => {
      if (event.type === "open") {
        trackEvent("virtual_shelf_open");
      } else {
        trackEvent("virtual_shelf_navigate", {
          direction: event.type === "previous" ? "prev" : "next",
        });
      }
    },
  );
  useEvent(
    LS2PAC_CONSTANTS.Events.onGoToFullDetailsFromVirtualShelf,
    (event: { resource: ResourceDWR }) => {
      trackEvent("virtual_shelf_select_item", {
        title: event.resource.shortTitle, // potentially high cardinality, consider omitting or tracking alternative metrics
      });
    },
  );

  function getDomainFromURL(url: string): string | null {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return null;
    }
  }

  const trackDownloadableClick = useCallback(
    (
      name:
        | "downloadable_checkout"
        | "downloadable_availability_click"
        | "downloadable_place_hold"
        | "downloadable_external_link_click",
      resource: ResourceView | ResourceDWR,
      source: AvailabilitySource,
    ) => {
      const providerLink = source?.downloadLink ?? resource.link;
      const providerDomain = getDomainFromURL(providerLink);
      trackEvent(name, {
        title: resource.shortTitle,
        provider_link: providerLink,
        provider_name: source?.service ?? providerDomain,
        provider_domain: providerDomain,
      });
    },
    [],
  );

  useEvent(
    LS2PAC_CONSTANTS.Events.onLoginResponse,
    (event: { success: boolean }) => {
      if (event.success) {
        // TODO confirm that user properties are set when this is called
        trackLogin();
      } else {
        // TODO track failed login attempts?
      }
    },
  );

  // TODO add method, i.e. google, guardian, local, sso
  const trackLogin = useCallback(() => {
    trackEvent("login");
  }, []);

  // TODO timed_out and non-interactive version
  const trackLogout = useCallback((timedOut: boolean = false) => {
    trackEvent("logout", { timed_out: timedOut });
  }, []);

  const contextValue = {
    trackEvent,
    trackPageView,
    trackSearch: trackKeywordSearch,
    trackLogin,
    trackLogout,
    trackExtendSearchSiteClick,
    trackAddFacetFilter,
    trackRemoveFacetFilter,
    trackClearFacetFilters,
    trackDownloadableClick,
    trackCancelHold,
    trackPlaceHold,
    trackReactivateHold,
    trackSuspendHold,
    trackLandingPageNavigate,
    trackLandingPageListChange,
    trackLandingPageJacketClick,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export function useAnalytics() {
  const context = React.useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
}
